import type { SmartSort } from '@iheartjane/dm-sdk';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useMemoizedRowInstance } from '@jane/bloom/hooks/dm/useMemoizedRowInstance';
import { useBloomBasename } from '@jane/bloom/hooks/useBloomBasename';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import type { MenuTopRowResponse } from '@jane/bloom/routes/api.menu-top-row/types';
import { logError } from '@jane/bloom/utils/logError';
import type { AlgoliaProduct } from '@jane/search/types';

const DEFAULT_REFRESH_TIME = 15;

interface UseMenuTopRowWithServerDataProps {
  /** Whether refetching is enabled */
  enableRefetch?: boolean;
  /** Initial server-side menuTopRow data (serialized JSON string) */
  initialMenuTopRow?: string;
  /** Refresh time in seconds (defaults to 15) */
  refreshTime?: number;
  /** Search filter string for client-side refetching. Required if enableRefetch is true. */
  searchFilter?: string;
  /** Store ID */
  storeId: number;
}

interface UseMenuTopRowWithServerDataResult {
  /** Whether there was an error */
  isError: boolean;
  /** Whether the query is currently fetching */
  isFetching: boolean;
  /** Whether the query was successful */
  isSuccess: boolean;
  /** Pause refetching */
  pauseRefetching: () => void;
  /** The current menu top row instance */
  rowInstance: SmartSort<AlgoliaProduct> | undefined;
  /** Unpause refetching */
  unpauseRefetching: () => void;
}

/**
 * Timer hook for menu top row refreshing
 */
const useMenuTopRowTimer = (
  timeInSeconds: number,
  enableRefetch: boolean,
  searchFilter: string | undefined
): {
  isTimerFinished: boolean;
  pauseTimer: () => void;
  restartTimer: () => void;
  unpauseTimer: () => void;
} => {
  const [timeLeft, setTimeLeft] = useState(timeInSeconds);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const pauseTimer = useCallback(() => {
    setIsPaused(true);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const unpauseTimer = useCallback(() => {
    setIsPaused(false);
  }, []);

  const restartTimer = useCallback(() => {
    setTimeLeft(timeInSeconds);
    setIsPaused(false);
  }, [timeInSeconds]);

  const canRefetch = enableRefetch && searchFilter !== undefined;

  useEffect(() => {
    if (timeLeft <= 0 || isPaused || !canRefetch) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        const newTime = prev - 1;
        if (newTime <= 0) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
        }
        return newTime;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [timeLeft, isPaused, canRefetch]);

  return {
    isTimerFinished: timeLeft <= 0,
    pauseTimer,
    restartTimer,
    unpauseTimer,
  };
};

/**
 * Hook that uses server-side initial data and implements client-side refreshing logic.
 * The initial load is server-side, and subsequent requests are client-side based on a timer.
 */
export const useMenuTopRowWithServerData = ({
  enableRefetch = true,
  initialMenuTopRow,
  refreshTime = 15,
  searchFilter,
  storeId,
}: UseMenuTopRowWithServerDataProps): UseMenuTopRowWithServerDataResult => {
  const { pinnedStore } = useRootLoaderData();
  const basename = useBloomBasename();

  // Background poll runs via plain fetch instead of useFetcher so transport-level
  // failures stay contained here and don't propagate to the route's ErrorBoundary.
  const [fetchState, setFetchState] = useState<'idle' | 'loading'>('idle');
  const [fetchedData, setFetchedData] = useState<
    MenuTopRowResponse | undefined
  >(undefined);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);

  // Deserialize the initial server-side data
  const initialRowInstance = useMemoizedRowInstance(initialMenuTopRow);

  // Deserialize fetched data when available (will be undefined until first successful refetch)
  // This is expected - the hook starts with server data and updates when client refetch completes
  const fetchedRowInstance = useMemoizedRowInstance(
    fetchedData && 'menuTopRow' in fetchedData && fetchedData.menuTopRow
      ? fetchedData.menuTopRow
      : undefined
  );

  // Get refresh time from store config if available
  const effectiveRefreshTime = useMemo(() => {
    if (refreshTime) return refreshTime;

    const refreshTimesBySurface = pinnedStore
      ? pinnedStore.menu_top_row_refresh_time
      : undefined;

    const time = refreshTimesBySurface?.['ecomm'];
    return time ? time / 1000 : DEFAULT_REFRESH_TIME;
  }, [refreshTime, pinnedStore]);

  // Timer for refreshing
  const { isTimerFinished, pauseTimer, restartTimer, unpauseTimer } =
    useMenuTopRowTimer(effectiveRefreshTime, enableRefetch, searchFilter);

  const [currentRowInstance, setCurrentRowInstance] = useState<
    SmartSort<AlgoliaProduct> | undefined
  >(initialRowInstance);
  const [shouldContinueRefetching, setShouldContinueRefetching] =
    useState(true);

  // Refs for tracking state
  const previousDataRef = useRef<SmartSort<AlgoliaProduct> | undefined>(
    initialRowInstance
  );
  const hasInitializedRef = useRef(false);
  const lastInitialMenuTopRowRef = useRef<string | undefined>(
    initialMenuTopRow
  );
  const hasFetchedRef = useRef(false);

  const updateRowInstance = useCallback(
    (instance: SmartSort<AlgoliaProduct>) => {
      setCurrentRowInstance(instance);
      previousDataRef.current = instance;
    },
    []
  );

  const shouldRefetchBasedOnProducts = useCallback(
    (instance: SmartSort<AlgoliaProduct> | undefined) => {
      return instance ? instance.products.length > 0 : false;
    },
    []
  );

  // Function to refetch menu top row
  const refetchMenuTopRow = useCallback(async () => {
    if (searchFilter === undefined) return;

    const excludeCreativeIds = previousDataRef.current?.['creativeIds'];
    const params = new URLSearchParams({
      searchFilter,
      storeId: String(storeId),
      ...(excludeCreativeIds && excludeCreativeIds.length > 0
        ? { excludeCreativeIds: JSON.stringify(excludeCreativeIds) }
        : {}),
    });

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setFetchState('loading');
    try {
      const response = await fetch(
        `${basename}api/menu-top-row?${params.toString()}`,
        { signal: controller.signal }
      );

      if (!response.ok) {
        const errorBody = await response.text();
        if (controller.signal.aborted) return;
        const errorMessage = errorBody || `HTTP ${response.status}`;
        logError(new Error(errorMessage), {
          hook: 'useMenuTopRowWithServerData',
          status: response.status,
        });
        setFetchedData({ error: errorMessage });
        return;
      }

      const json = (await response.json()) as MenuTopRowResponse;
      if (controller.signal.aborted) return;
      setFetchedData(json);
    } catch (error) {
      if (controller.signal.aborted) return;
      logError(error, { hook: 'useMenuTopRowWithServerData' });
    } finally {
      if (abortControllerRef.current === controller && isMountedRef.current) {
        setFetchState('idle');
      }
    }
  }, [searchFilter, storeId, basename]);

  const pauseRefetching = useCallback(() => {
    // If refetch is not enabled or search filter is not defined, do not pause the timer
    if (!enableRefetch || searchFilter === undefined) return;
    pauseTimer();
  }, [pauseTimer, enableRefetch, searchFilter]);

  const unpauseRefetching = useCallback(() => {
    // If refetch is not enabled or search filter is not defined, do not unpause the timer
    if (!enableRefetch || searchFilter === undefined) return;
    if (shouldContinueRefetching) {
      unpauseTimer();
    } else {
      pauseTimer();
    }
  }, [
    unpauseTimer,
    shouldContinueRefetching,
    pauseTimer,
    enableRefetch,
    searchFilter,
  ]);

  // Handle fetched row instance updates
  const handleFetchedRowInstance = useCallback(() => {
    if (!fetchedRowInstance) return;

    hasFetchedRef.current = true;
    const hasProducts = fetchedRowInstance.products.length > 0;

    if (hasProducts) {
      updateRowInstance(fetchedRowInstance);
      setShouldContinueRefetching(true);
    } else {
      // If no products returned, stop refetching
      setShouldContinueRefetching(false);
      pauseTimer();
    }
  }, [fetchedRowInstance, pauseTimer, updateRowInstance]);

  // Handle refetch decision and timer management
  const handleRefetchDecision = useCallback(
    (instance: SmartSort<AlgoliaProduct>) => {
      if (!hasFetchedRef.current) {
        // If we haven't fetched yet, check if we should refetch based on products
        const shouldRefetch = shouldRefetchBasedOnProducts(instance);
        setShouldContinueRefetching(shouldRefetch);
        if (shouldRefetch) {
          restartTimer();
        } else {
          pauseTimer();
        }
      } else {
        // If we've already fetched, restart the timer
        restartTimer();
      }
    },
    [restartTimer, pauseTimer, shouldRefetchBasedOnProducts]
  );

  // Initialize with server data and restart timer when searchFilter or initialMenuTopRow changes
  const handleInitialization = useCallback(() => {
    if (!initialRowInstance) return;

    const dataChanged = lastInitialMenuTopRowRef.current !== initialMenuTopRow;
    const isSearchFilterValid = searchFilter !== undefined;
    const canRefetch = enableRefetch && isSearchFilterValid;

    // Reset state when data changes (new page/route)
    if (dataChanged) {
      lastInitialMenuTopRowRef.current = initialMenuTopRow;
      hasInitializedRef.current = false;
      hasFetchedRef.current = false;
    }

    // Handle case where we can't refetch
    if (!canRefetch) {
      pauseTimer();
      return;
    }

    // Initialize only once per data change
    if (!hasInitializedRef.current) {
      updateRowInstance(initialRowInstance);
      if (!hasFetchedRef.current) {
        handleRefetchDecision(initialRowInstance);
      }
      hasInitializedRef.current = true;
    } else if (dataChanged) {
      // When data changes after initialization, update instance and handle refetch decision
      updateRowInstance(initialRowInstance);
      handleRefetchDecision(initialRowInstance);
    }
  }, [
    searchFilter,
    enableRefetch,
    pauseTimer,
    initialMenuTopRow,
    initialRowInstance,
    updateRowInstance,
    handleRefetchDecision,
  ]);

  // Refetch when timer finishes
  const handleTimerFinish = useCallback(() => {
    if (!isTimerFinished) return;

    const canRefetch =
      enableRefetch && shouldContinueRefetching && searchFilter !== undefined;

    if (canRefetch) {
      refetchMenuTopRow();
      restartTimer();
    } else {
      // If we can't refetch, pause the timer to prevent it from continuing
      pauseTimer();
    }
  }, [
    isTimerFinished,
    enableRefetch,
    shouldContinueRefetching,
    searchFilter,
    refetchMenuTopRow,
    restartTimer,
    pauseTimer,
  ]);

  // Initialize with server data and restart timer when searchFilter or initialMenuTopRow changes
  useEffect(() => {
    handleInitialization();
  }, [handleInitialization]);

  // Refetch when timer finishes
  useEffect(() => {
    handleTimerFinish();
  }, [handleTimerFinish]);

  // Update current row instance when fetched data is available
  useEffect(() => {
    handleFetchedRowInstance();
  }, [handleFetchedRowInstance]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Abort in-flight requests on unmount or when filter/store changes,
  // so stale results can't overwrite fresh data after a filter change.
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, [searchFilter, storeId]);

  // Check for errors in the loader response
  const hasError = useMemo(
    () =>
      fetchState === 'idle' &&
      fetchedData !== undefined &&
      'error' in fetchedData,
    [fetchState, fetchedData]
  );

  const isFetching = fetchState === 'loading';
  const isSuccess = !!currentRowInstance && !hasError && !isFetching;

  return {
    isError: hasError,
    isFetching,
    isSuccess,
    pauseRefetching,
    rowInstance: currentRowInstance,
    unpauseRefetching,
  };
};
